import { useMemo } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import {
  useDeleteGoogleBusinessAccountLocation,
  useGetGoogleBusinessAccountLocations,
} from "@/business/google-business-query";

interface DeleteGoogleBusinessLocationModalProps {
  businessId: string;
  locationId: string | null;
  onClose: () => void;
}

export const DeleteGoogleBusinessLocationModal = ({
  businessId,
  locationId,
  onClose,
}: DeleteGoogleBusinessLocationModalProps) => {
  const { data: accountLocations = [] } =
    useGetGoogleBusinessAccountLocations(businessId);

  const { mutate: deleteLocation, isPending: isDeleting } =
    useDeleteGoogleBusinessAccountLocation(businessId);

  const isOpen = !!locationId;

  const location = useMemo(
    () => accountLocations.find((loc) => loc.id === locationId),
    [accountLocations, locationId],
  );

  const handleConfirm = () => {
    if (!locationId) return;

    deleteLocation(locationId, {
      onSuccess: () => {
        toast.success("Location deleted successfully");
        onClose();
      },
      onError: (error) => {
        console.error("Failed to delete location:", error);
        toast.error("Failed to delete location", {
          description:
            error instanceof Error ? error.message : "Please try again",
        });
      },
    });
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Google Business location</DialogTitle>
        </DialogHeader>
        <Stack align="start" className="w-full" gap={6}>
          <Text tone="muted">
            Are you sure you want to delete the connection to the following
            location?
          </Text>
          {location && (
            <Stack className="w-full" align="start" gap={2}>
              <Text variant="label">{location.title}</Text>
              <Text tone="muted">{location.location}</Text>
              <Text tone="muted">Account: {location.account}</Text>
            </Stack>
          )}
        </Stack>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isDeleting}
            loading={isDeleting}
          >
            Delete location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
